/**
 * Supabase Realtime — conscious_events INSERT 구독 → NotificationStore 갱신
 */

import { useEffect } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';

import { logger } from '@shared/logger';
import { getSupabase } from '../../services/supabaseClient';
import { useNotificationStore, type AppNotification } from '../../stores/NotificationStore';
import { isSupabaseRealtimeAvailable } from '../../utils/supabaseRealtimeEnv';

const NEURO_DELIVERED_EVENT_TYPE = 'conscious:neuro:delivered';

interface ConsciousEventPayload {
  signalType?: string;
  message?: string;
  title?: string;
  intensity?: number;
}

function resolvePriority(intensity: number | undefined): AppNotification['priority'] {
  if (intensity === undefined || Number.isNaN(intensity)) return 'medium';
  if (intensity > 0.8) return 'critical';
  if (intensity > 0.6) return 'high';
  if (intensity < 0.3) return 'low';
  return 'medium';
}

function mapRecordToNotification(record: {
  id: string;
  event_type: string;
  payload: ConsciousEventPayload | null;
  created_at: string;
}): AppNotification | null {
  if (record.event_type !== NEURO_DELIVERED_EVENT_TYPE) return null;

  const payload = record.payload ?? {};
  return {
    id: record.id,
    type: payload.signalType ?? 'unknown',
    message: payload.message ?? '',
    ...(payload.title !== undefined ? { title: payload.title } : {}),
    priority: resolvePriority(payload.intensity),
    receivedAt: new Date(record.created_at),
    read: false,
  };
}

export function useRealtimeNotifications(tenantId: string): void {
  const addNotification = useNotificationStore((state) => state.addNotification);

  useEffect(() => {
    if (!tenantId || !isSupabaseRealtimeAvailable()) return;

    let channel: RealtimeChannel | null = null;

    const setup = async (): Promise<void> => {
      try {
        const supabase = getSupabase();
        channel = supabase
          .channel(`conscious-events-${tenantId}`)
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'conscious_events',
              filter: `tenant_id=eq.${tenantId}`,
            },
            (payload) => {
              const record = payload.new as {
                id: string;
                event_type: string;
                payload: ConsciousEventPayload | null;
                created_at: string;
              };
              const notification = mapRecordToNotification(record);
              if (notification) {
                addNotification(notification);
              }
            }
          )
          .subscribe((status) => {
            if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
              logger.warn('[useRealtimeNotifications] subscription error', { data: { status } });
            }
          });
      } catch (error) {
        logger.warn('[useRealtimeNotifications] subscription setup failed', {
          data: error instanceof Error ? error.message : String(error),
        });
      }
    };

    void setup();

    return () => {
      if (channel) {
        void getSupabase().removeChannel(channel);
      }
    };
  }, [tenantId, addNotification]);
}
